import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

// Weighted heatmap of building centroids. Weight = registered member count,
// falling back to 1 per building so density-of-buildings still shows something
// meaningful before the members_count endpoint returns.
export const HeatmapLayer = ({ buildings, memberCounts, visible }) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    if (!buildings || !buildings.features || buildings.features.length === 0) {
      return;
    }

    const points = [];
    let maxWeight = 1;

    for (const f of buildings.features) {
      const coords = f.geometry && f.geometry.coordinates;
      if (!coords) continue;

      let lat, lng;
      if (f.geometry.type === 'Point') {
        [lng, lat] = coords;
      } else if (f.properties && f.properties.building_centroid) {
        // pg_featureserv may nest centroid in properties for polygon features
        const c = f.properties.building_centroid;
        if (c.coordinates) {
          [lng, lat] = c.coordinates;
        }
      } else if (f.geometry.type === 'MultiPolygon' || f.geometry.type === 'Polygon') {
        // Fall back to centroid of first ring — good enough for a heatmap
        try {
          const ring = f.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0];
          const lats = ring.map(c => c[1]);
          const lngs = ring.map(c => c[0]);
          lat = (Math.min(...lats) + Math.max(...lats)) / 2;
          lng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        } catch (e) { continue; }
      }

      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) continue;

      const bid = f.properties && f.properties.id;
      const raw = memberCounts && bid != null ? memberCounts[bid] : undefined;
      const weight = typeof raw === 'number' && raw > 0 ? raw : 1;
      if (weight > maxWeight) maxWeight = weight;

      points.push([lat, lng, weight]);
    }

    if (points.length === 0) return;

    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }

    layerRef.current = L.heatLayer(points, {
      radius: 22,
      blur: 18,
      maxZoom: 17,
      max: Math.max(maxWeight, 10),
      minOpacity: 0.35,
      gradient: {
        0.0: '#0ea5e9',   // sparse — cool blue
        0.25: '#22c55e',  // light green
        0.5: '#eab308',   // yellow
        0.7: '#f97316',   // orange
        1.0: '#dc2626',   // dense — hot red
      },
    });

    layerRef.current.addTo(map);

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [buildings, memberCounts, visible, map]);

  return null;
};
